// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A form component for resolving detail-related asset exceptions.
 *
 * Displays two types of detail exceptions:
 * 1. Simple fields (useful life, salvage value):
 *    - Editable fields with inline save buttons
 *    - Each field can be saved independently
 *    - Loading/success/error feedback per field
 *    - Live validation showing if exception is resolved
 *
 * 2. Complex fields (cost basis - inventory and deployed):
 *    - Informational message directing users to resolve on asset details page
 *    - Link to asset details accounting tab
 *    - Explanation of why resolution must happen elsewhere
 *    - Cost basis can be simple or advanced with multiple components
 */
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import {
  UpdateAssetAccountingPropertiesParameters,
  updateAssetAccountingProperties,
} from '@/asset/api/update-asset-accounting-properties';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { ExceptionType } from '@/constants/asset-exception-type';
import {
  AutoAwesomeOutlined,
  CheckCircle,
  Error,
  OpenInNew,
} from '@/constants/hardfin-standard-icons';
import { Currency, currencyInputSchema } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import {
  currencySymbolForIso,
  formatAmountWithCurrency,
} from '@/currency/utilities/format';
import { toCurrencyPasteResult } from '@/currency/utilities/paste';
import { toSerializableCurrency } from '@/currency/utilities/to-form-value';
import { getMinorUnitDigits } from '@/currency/utilities/validate';
import { ItemAccounting } from '@/items/api/types';
import { DetailExceptionFormData } from '@/report/api/types';
import { getExceptionConfig } from '@/report/utilities/exception-config';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ClipboardEvent, useEffect, useState } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

/**
 * Maps backend error codes to the corresponding form field names.
 * Used to display field-specific error messages from the API.
 */
const ERROR_CODE_TO_FIELD: Record<number, keyof DetailExceptionFormData> = {
  [ERROR_CODE_MAPPING.accounting_invalid_salvage_value]: 'salvageValue',
  [ERROR_CODE_MAPPING.accounting_invalid_useful_life]: 'usefulLife',
  [ERROR_CODE_MAPPING.accounting_invalid_simple_cost_basis]:
    'inventoryCostBasis',
};

/**
 * Props for the DetailExceptionResolutionForm component.
 */
interface Props {
  /**
   * Array of exception types to display and resolve.
   * Each exception will be shown with relevant detail fields.
   */
  exceptionTypes: ExceptionType[];

  /**
   * Item defaults (accounting properties) to suggest to the user.
   * May be null if item defaults are not available.
   */
  itemDefaults: ItemAccounting | null;

  /**
   * React Hook Form control for managing form state.
   */
  control: Control<DetailExceptionFormData>;

  /**
   * React Hook Form setValue function for updating field values programmatically.
   */
  setValue: UseFormSetValue<DetailExceptionFormData>;

  /**
   * The asset key used for making individual field update API calls.
   */
  assetKey: string;

  /**
   * The asset's cost currency, applied to the cost values sent on save.
   */
  costCurrencyCode: IsoCurrencyCode;

  /**
   * The asset serial number used in informational messages.
   */
  assetSerial: string;

  /**
   * Detail data for the asset containing all detail fields.
   * Used to display current values and validate exceptions.
   */
  detailData: {
    inServiceDate: string | null;
    usefulLife: number | null;
    inventoryCostBasis: string | null;
    deployedCostBasis: string | null;
    salvageValue: string | null;
  };
}

/**
 * Determines if the missing cost basis exception has been resolved.
 */
function isMissingCostBasisResolved(
  inventoryCostBasis: string | null
): boolean {
  return inventoryCostBasis !== null && inventoryCostBasis !== '';
}

/**
 * Determines if the missing useful life exception has been resolved.
 */
function isMissingUsefulLifeResolved(usefulLife: string | null): boolean {
  return usefulLife !== null && usefulLife !== '';
}

/**
 * Determines if the deployment without acquisition exception has been resolved.
 * This exception occurs when an asset has deployment costs (freight_outbound or installation)
 * but no acquisition costs (simple_cost_basis or bill_of_materials).
 */
function isDeploymentWithoutAcquisitionResolved(
  inventoryCostBasis: string | null
): boolean {
  // If inventory cost basis is set, the acquisition cost exists and the exception is resolved
  return inventoryCostBasis !== null && inventoryCostBasis !== '';
}

/**
 * Determines if the salvage exceeds 50% exception has been resolved.
 */
function isSalvageExceptionResolved(
  salvageValue: string | null,
  deployedCostBasis: string | null
): boolean {
  // If salvage value is not set, it is resolved (to 0)
  if (!salvageValue) {
    return true;
  }

  // If salvage value is set but deployed cost basis is not set, it is not resolved
  if (!deployedCostBasis) {
    return false;
  }

  const salvage = parseFloat(salvageValue);
  const cost = parseFloat(deployedCostBasis);

  if (isNaN(salvage) || isNaN(cost)) {
    return false;
  }

  return salvage < 0.5 * cost;
}

/** A field value to save, with its validated currency for a money field. */
interface FieldSaveValue {
  value: string | null;
  currency: Currency | null;
}

/**
 * Component for rendering an editable detail field with an inline save button.
 */
function EditableDetailField(props: {
  fieldName: keyof DetailExceptionFormData;
  fieldLabel: string;
  control: Control<DetailExceptionFormData>;
  setValue: UseFormSetValue<DetailExceptionFormData>;
  assetKey: string;
  // An ISO code denominates the field; undefined marks a field that is not money at all.
  costCurrencyCode: IsoCurrencyCode | undefined;
  originalValue: string | null;
  onSaveSuccess: () => void;
  itemDefault?: number | string | null;
}) {
  const queryClient = useQueryClient();
  const formValues = useWatch({ control: props.control });
  const currentValue = formValues[props.fieldName] ?? null;

  // Track the last successfully saved value
  const [lastSavedValue, setLastSavedValue] = useState<string | null>(
    props.originalValue
  );

  // Track error state for save failures
  const [hasError, setHasError] = useState(false);
  // Track the value at the time of error to detect changes after error
  const [errorValue, setErrorValue] = useState<string | null>(null);
  // Track the error message from the backend
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Determine if the value has changed from the last saved value
  const hasChanged = currentValue !== lastSavedValue;

  // Clear error state when user changes the value after an error occurred
  useEffect(() => {
    if (hasError && currentValue !== errorValue) {
      setHasError(false);
      setErrorValue(null);
      setErrorMessage(null);
    }
  }, [currentValue, hasError, errorValue]);

  // Shows a field error that clears once the field moves off the value.
  const showFieldError = (message: string, erroredValue: string | null) => {
    setHasError(true);
    setErrorValue(erroredValue);
    setErrorMessage(message);
  };

  // Create mutation for saving individual field
  const mutation = useMutation({
    mutationFn: async (fieldValue: FieldSaveValue) => {
      const updates: UpdateAssetAccountingPropertiesParameters = {};
      const money = fieldValue.currency;

      if (props.fieldName === 'usefulLife') {
        updates.usefulLife = fieldValue.value
          ? parseInt(fieldValue.value, 10)
          : null;
      } else if (props.fieldName === 'inventoryCostBasis') {
        updates.simpleCostBasis = money;
      } else if (props.fieldName === 'salvageValue') {
        updates.salvageValue = money;
      }

      await updateAssetAccountingProperties(props.assetKey, updates);
      return fieldValue.value;
    },
    onSuccess: (savedValue) => {
      // Update the last saved value to match what was just saved
      setLastSavedValue(savedValue);
      // Clear any error state
      setHasError(false);
      setErrorValue(null);
      setErrorMessage(null);
      // Invalidate the single-asset query so the dialog can detect resolution
      // Note: We intentionally don't invalidate the main detail-inconsistencies query
      // here to avoid query thrashing when resolving multiple assets quickly.
      // The main query refreshes on tab switch or window focus.
      void queryClient.invalidateQueries({
        queryKey: ['asset', props.assetKey],
      });
      // Invalidate only detail-inconsistencies queries that are filtered by assetIds.
      // This allows the dialog's single-asset query to refresh without triggering
      // a full refetch of the unfiltered main query (which is expensive with 55k+ rows).
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as unknown[];
          if (
            key[0] === 'report' &&
            key[1] === 'exception' &&
            key[2] === 'asset' &&
            key[3] === 'detail-inconsistencies'
          ) {
            const params = key[4] as { assetIds?: string[] } | undefined;
            return (
              Array.isArray(params?.assetIds) && params.assetIds.length > 0
            );
          }
          return false;
        },
      });
      props.onSaveSuccess();
    },
    onError: (error) => {
      // Extract error information from the backend response
      let backendErrorMessage = 'Save failed, please try again';

      if (error instanceof AxiosError) {
        const wellFormedError = handleAPIError(error);
        const errorCode = wellFormedError.apiStatusCode;

        // Check if this error code applies to the current field
        const fieldForError = ERROR_CODE_TO_FIELD[errorCode];
        if (fieldForError === props.fieldName && wellFormedError.message) {
          // Use the backend error message for this field
          backendErrorMessage =
            wellFormedError.message.charAt(0).toUpperCase() +
            wellFormedError.message.slice(1);
        }
      }

      setHasError(true);
      setErrorValue(currentValue);
      setErrorMessage(backendErrorMessage);
    },
  });

  // A money value finer than the currency's minor unit is refused before
  // it is sent, with the schema's message under the field.
  const saveValue = (value: string | null) => {
    if (props.costCurrencyCode === undefined || !value) {
      mutation.mutate({ value, currency: null });
      return;
    }

    const parsed = currencyInputSchema.safeParse({
      amount: value,
      isoCode: props.costCurrencyCode,
    });
    if (!parsed.success) {
      showFieldError(parsed.error.issues[0].message, value);
      return;
    }

    mutation.mutate({ value, currency: parsed.data });
  };

  const handleSave = () => {
    saveValue(currentValue);
  };

  // A money paste replaces the value in the asset's cost currency, or is
  // rejected with a message instead of being cut down to the allowed decimals.
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    if (props.costCurrencyCode === undefined) {
      return;
    }

    event.preventDefault();
    const pasted = toCurrencyPasteResult(event.clipboardData.getData('text'), {
      isoCode: props.costCurrencyCode,
      selectableCurrencyCodes: [props.costCurrencyCode],
      lockTo: props.costCurrencyCode,
    });
    if (pasted.kind === 'rejected') {
      showFieldError(pasted.message, currentValue);
      return;
    }

    props.setValue(props.fieldName, pasted.amount, { shouldDirty: true });
  };

  const handleApplyRecommendation = () => {
    if (props.itemDefault !== null && props.itemDefault !== undefined) {
      const defaultValue = String(props.itemDefault);
      // Update the form field value first so UI updates immediately
      props.setValue(props.fieldName, defaultValue, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      // Then save to backend
      saveValue(defaultValue);
    }
  };

  // Format the item default value for display
  const formatDefaultValue = (value: number | string): string => {
    if (props.costCurrencyCode !== undefined) {
      return formatAmountWithCurrency(String(value), props.costCurrencyCode);
    }
    return `${value} months`;
  };

  // Check if current value matches the item default
  const currentValueMatchesDefault =
    props.itemDefault !== null &&
    props.itemDefault !== undefined &&
    currentValue !== null &&
    String(props.itemDefault) === currentValue;

  // Show AI recommendation if:
  // 1. Item default exists
  // 2. Current value doesn't match the default (not redundant)
  const showAIRecommendation =
    props.itemDefault !== null &&
    props.itemDefault !== undefined &&
    !currentValueMatchesDefault;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box flex={1}>
          <Stack spacing={0}>
            <FormNumberTextField
              fullWidth
              name={props.fieldName}
              label={props.fieldLabel}
              control={props.control}
              isCurrency={props.costCurrencyCode !== undefined}
              maximumFractionDigits={
                props.costCurrencyCode !== undefined
                  ? getMinorUnitDigits(props.costCurrencyCode)
                  : undefined
              }
              allowNegative={false}
              InputProps={{
                onPaste: handlePaste,
                // Currency amounts are denominated in the asset's cost currency.
                startAdornment:
                  props.costCurrencyCode !== undefined ? (
                    <InputAdornment position="start">
                      {currencySymbolForIso(props.costCurrencyCode)}
                    </InputAdornment>
                  ) : undefined,
                sx: hasError
                  ? {
                      '& fieldset': {
                        borderColor: 'error.main',
                        borderWidth: 2,
                      },
                    }
                  : undefined,
              }}
              InputLabelProps={
                hasError
                  ? {
                      sx: {
                        color: 'error.main',
                      },
                    }
                  : undefined
              }
            />
            {hasError && errorMessage && (
              <Typography
                variant="caption"
                color="error.main"
                sx={{ marginTop: 0.5, marginLeft: 1.75 }}
              >
                {errorMessage}
              </Typography>
            )}
          </Stack>
        </Box>
        <Tooltip
          arrow
          title={!hasChanged ? 'No changes to save' : ''}
          placement="top"
        >
          <Box
            component="span"
            sx={{ alignSelf: 'flex-start', display: 'flex' }}
          >
            <Button
              variant="contained"
              color={hasError ? 'error' : 'primary'}
              onClick={handleSave}
              disabled={mutation.isPending || !hasChanged}
              aria-label={!hasChanged ? 'No changes to save' : 'Save'}
            >
              {mutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Tooltip>
      </Stack>

      {/* Hardfin AI recommendation */}
      {showAIRecommendation && (
        <Alert
          severity="info"
          icon={
            <AutoAwesomeOutlined fontSize="small" sx={{ color: 'info.main' }} />
          }
          sx={{
            paddingY: 0.5,
            alignItems: 'center',
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="body2">
              Hardfin AI recommends:{' '}
              {props.itemDefault !== null &&
                props.itemDefault !== undefined &&
                formatDefaultValue(props.itemDefault)}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleApplyRecommendation}
              disabled={mutation.isPending}
              startIcon={
                <AutoAwesomeOutlined fontSize="small" sx={{ fontSize: 16 }} />
              }
            >
              {mutation.isPending ? 'Applying...' : 'Use recommendation'}
            </Button>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

/**
 * Form component for resolving detail-related asset exceptions.
 * Displays each exception with its relevant detail fields and provides
 * item default suggestions when available.
 *
 * Features:
 * - Individual field save buttons
 * - Live validation feedback
 * - Item default suggestions
 * - Real-time resolution status
 * - Clear helper text for each exception
 * - Independent field saves
 *
 * Usage example:
 * ```tsx
 * <DetailExceptionResolutionForm
 *   exceptionTypes={['MISSING_USEFUL_LIFE']}
 *   itemDefaults={item.accounting}
 *   control={control}
 *   assetKey="asset-123"
 *   assetSerial="SN-12345"
 *   detailData={detailData}
 * />
 * ```
 */
export function DetailExceptionResolutionForm(props: Props) {
  const { user } = useCurrentUser();

  // Watch form values for real-time validation
  const formValues = useWatch({
    control: props.control,
  });

  // Suggesting the item's default onto an asset in a different currency would transmute the amount, so it only shows on a currency match
  const itemDefaultCurrencyCode =
    props.itemDefaults?.costCurrencyCode ?? user.organization.defaultCurrency;
  const itemSalvageValue = props.itemDefaults?.salvageValue ?? null;
  const salvageValueDefault =
    itemSalvageValue !== null &&
    itemDefaultCurrencyCode === props.costCurrencyCode
      ? toSerializableCurrency(itemSalvageValue).amount
      : null;

  // Callback for handling successful saves
  const handleSaveSuccess = () => {
    // Additional success handling can be added here if needed
  };

  return (
    <Stack spacing={3}>
      {/* MISSING_USEFUL_LIFE exception */}
      {props.exceptionTypes.includes('MISSING_USEFUL_LIFE') && (
        <Paper
          variant="outlined"
          sx={{
            padding: 2,
            backgroundColor: isMissingUsefulLifeResolved(
              formValues.usefulLife ?? null
            )
              ? 'success.50'
              : 'grey.50',
            borderColor: isMissingUsefulLifeResolved(
              formValues.usefulLife ?? null
            )
              ? 'success.main'
              : 'error.main',
            borderWidth: 2,
          }}
        >
          <Stack spacing={2}>
            {/* Exception header with icon and label */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: isMissingUsefulLifeResolved(
                    formValues.usefulLife ?? null
                  )
                    ? 'success.main'
                    : 'error.main',
                }}
              >
                {getExceptionConfig('MISSING_USEFUL_LIFE').icon}
              </Box>
              <Typography
                variant="subtitle2"
                color={
                  isMissingUsefulLifeResolved(formValues.usefulLife ?? null)
                    ? 'success.main'
                    : 'error.main'
                }
              >
                {getExceptionConfig('MISSING_USEFUL_LIFE').label}
              </Typography>
            </Stack>

            {/* Editable useful life field */}
            <EditableDetailField
              fieldName="usefulLife"
              fieldLabel="Useful life (months)"
              control={props.control}
              setValue={props.setValue}
              assetKey={props.assetKey}
              costCurrencyCode={undefined}
              originalValue={
                props.detailData.usefulLife !== null
                  ? String(props.detailData.usefulLife)
                  : null
              }
              onSaveSuccess={handleSaveSuccess}
              itemDefault={props.itemDefaults?.usefulLife}
            />

            {/* Helper text with validation status */}
            <Alert
              severity={
                isMissingUsefulLifeResolved(formValues.usefulLife ?? null)
                  ? 'success'
                  : 'error'
              }
              icon={
                isMissingUsefulLifeResolved(formValues.usefulLife ?? null) ? (
                  <CheckCircle />
                ) : (
                  <Error />
                )
              }
            >
              <Typography variant="body2">
                {isMissingUsefulLifeResolved(formValues.usefulLife ?? null)
                  ? 'Useful life is valid'
                  : 'Assets with an in-service date require a useful life'}
              </Typography>
            </Alert>
          </Stack>
        </Paper>
      )}

      {/* MISSING_COST_BASIS exception */}
      {props.exceptionTypes.includes('MISSING_COST_BASIS') && (
        <Paper
          variant="outlined"
          sx={{
            padding: 2,
            backgroundColor: isMissingCostBasisResolved(
              formValues.inventoryCostBasis ?? null
            )
              ? 'success.50'
              : 'warning.50',
            borderColor: isMissingCostBasisResolved(
              formValues.inventoryCostBasis ?? null
            )
              ? 'success.main'
              : 'warning.main',
            borderWidth: 2,
          }}
        >
          <Stack spacing={2}>
            {/* Exception header with icon and label */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: isMissingCostBasisResolved(
                    formValues.inventoryCostBasis ?? null
                  )
                    ? 'success.main'
                    : 'warning.main',
                }}
              >
                {getExceptionConfig('MISSING_COST_BASIS').icon}
              </Box>
              <Typography
                variant="subtitle2"
                color={
                  isMissingCostBasisResolved(
                    formValues.inventoryCostBasis ?? null
                  )
                    ? 'success.main'
                    : 'warning.main'
                }
              >
                {getExceptionConfig('MISSING_COST_BASIS').label}
              </Typography>
            </Stack>

            {/* Current cost basis display */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  Inventory cost basis
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formValues.inventoryCostBasis
                    ? formatAmountWithCurrency(
                        formValues.inventoryCostBasis,
                        props.costCurrencyCode
                      )
                    : 'Not set'}
                </Typography>
              </Box>
            </Stack>

            {/* Helper text with validation status and link */}
            <Alert
              severity={
                isMissingCostBasisResolved(
                  formValues.inventoryCostBasis ?? null
                )
                  ? 'success'
                  : 'warning'
              }
              icon={
                isMissingCostBasisResolved(
                  formValues.inventoryCostBasis ?? null
                ) ? (
                  <CheckCircle />
                ) : (
                  <Error />
                )
              }
            >
              <Typography variant="body2">
                {isMissingCostBasisResolved(
                  formValues.inventoryCostBasis ?? null
                ) ? (
                  'Cost basis is valid'
                ) : (
                  <>
                    Assets require a cost basis unless scrapped. Update{' '}
                    <Link
                      href={`/assets/${props.assetKey}/accounting`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                      }}
                    >
                      accounting details for {props.assetSerial}
                      <OpenInNew fontSize="inherit" />
                    </Link>{' '}
                    to configure cost basis.
                  </>
                )}
              </Typography>
            </Alert>
          </Stack>
        </Paper>
      )}

      {/* DEPLOYMENT_WITHOUT_ACQUISITION exception */}
      {props.exceptionTypes.includes('DEPLOYMENT_WITHOUT_ACQUISITION') && (
        <Paper
          variant="outlined"
          sx={{
            padding: 2,
            backgroundColor: isDeploymentWithoutAcquisitionResolved(
              formValues.inventoryCostBasis ?? null
            )
              ? 'success.50'
              : 'warning.50',
            borderColor: isDeploymentWithoutAcquisitionResolved(
              formValues.inventoryCostBasis ?? null
            )
              ? 'success.main'
              : 'warning.main',
            borderWidth: 2,
          }}
        >
          <Stack spacing={2}>
            {/* Exception header with icon and label */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: isDeploymentWithoutAcquisitionResolved(
                    formValues.inventoryCostBasis ?? null
                  )
                    ? 'success.main'
                    : 'warning.main',
                }}
              >
                {getExceptionConfig('DEPLOYMENT_WITHOUT_ACQUISITION').icon}
              </Box>
              <Typography
                variant="subtitle2"
                color={
                  isDeploymentWithoutAcquisitionResolved(
                    formValues.inventoryCostBasis ?? null
                  )
                    ? 'success.main'
                    : 'warning.main'
                }
              >
                {getExceptionConfig('DEPLOYMENT_WITHOUT_ACQUISITION').label}
              </Typography>
            </Stack>

            {/* Current cost basis display */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  Inventory cost basis
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formValues.inventoryCostBasis
                    ? formatAmountWithCurrency(
                        formValues.inventoryCostBasis,
                        props.costCurrencyCode
                      )
                    : 'Not set'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                →
              </Typography>
              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  Deployed cost basis
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formValues.deployedCostBasis
                    ? formatAmountWithCurrency(
                        formValues.deployedCostBasis,
                        props.costCurrencyCode
                      )
                    : 'Not set'}
                </Typography>
              </Box>
            </Stack>

            {/* Complex exception message */}
            <Alert
              severity={
                isDeploymentWithoutAcquisitionResolved(
                  formValues.inventoryCostBasis ?? null
                )
                  ? 'success'
                  : 'warning'
              }
              icon={
                isDeploymentWithoutAcquisitionResolved(
                  formValues.inventoryCostBasis ?? null
                ) ? (
                  <CheckCircle />
                ) : (
                  <Error />
                )
              }
            >
              <Typography variant="body2">
                {isDeploymentWithoutAcquisitionResolved(
                  formValues.inventoryCostBasis ?? null
                ) ? (
                  'Acquisition costs are valid'
                ) : (
                  <>
                    Acquisition costs required when deployment costs are set.
                    Update{' '}
                    <Link
                      href={`/assets/${props.assetKey}/accounting`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                      }}
                    >
                      accounting details for {props.assetSerial}
                      <OpenInNew fontSize="inherit" />
                    </Link>{' '}
                    to configure cost basis.
                  </>
                )}
              </Typography>
            </Alert>
          </Stack>
        </Paper>
      )}

      {/* SALVAGE_EXCEEDS_50_PERCENT exception */}
      {props.exceptionTypes.includes('SALVAGE_EXCEEDS_50_PERCENT') && (
        <Paper
          variant="outlined"
          sx={{
            padding: 2,
            backgroundColor: isSalvageExceptionResolved(
              formValues.salvageValue ?? null,
              formValues.deployedCostBasis ?? null
            )
              ? 'success.50'
              : 'grey.50',
            borderColor: isSalvageExceptionResolved(
              formValues.salvageValue ?? null,
              formValues.deployedCostBasis ?? null
            )
              ? 'success.main'
              : 'error.main',
            borderWidth: 2,
          }}
        >
          <Stack spacing={2}>
            {/* Exception header with icon and label */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: isSalvageExceptionResolved(
                    formValues.salvageValue ?? null,
                    formValues.deployedCostBasis ?? null
                  )
                    ? 'success.main'
                    : 'error.main',
                }}
              >
                {getExceptionConfig('SALVAGE_EXCEEDS_50_PERCENT').icon}
              </Box>
              <Typography
                variant="subtitle2"
                color={
                  isSalvageExceptionResolved(
                    formValues.salvageValue ?? null,
                    formValues.deployedCostBasis ?? null
                  )
                    ? 'success.main'
                    : 'error.main'
                }
              >
                {getExceptionConfig('SALVAGE_EXCEEDS_50_PERCENT').label}
              </Typography>
            </Stack>

            {/* Cost basis to salvage value comparison display */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  Deployed cost basis
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formValues.deployedCostBasis
                    ? formatAmountWithCurrency(
                        formValues.deployedCostBasis,
                        props.costCurrencyCode
                      )
                    : 'Not set'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                →
              </Typography>
              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  Salvage value
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formValues.salvageValue
                    ? formatAmountWithCurrency(
                        formValues.salvageValue,
                        props.costCurrencyCode
                      )
                    : 'Not set'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {formValues.salvageValue &&
                formValues.deployedCostBasis &&
                Number(formValues.deployedCostBasis) > 0
                  ? `(${(
                      (Number(formValues.salvageValue) /
                        Number(formValues.deployedCostBasis)) *
                      100
                    ).toFixed(1)}% of cost basis)`
                  : ''}
              </Typography>
            </Stack>

            {/* Editable salvage value field */}
            <EditableDetailField
              fieldName="salvageValue"
              fieldLabel="Salvage value"
              control={props.control}
              setValue={props.setValue}
              assetKey={props.assetKey}
              costCurrencyCode={props.costCurrencyCode}
              originalValue={props.detailData.salvageValue}
              onSaveSuccess={handleSaveSuccess}
              itemDefault={salvageValueDefault}
            />

            {/* Helper text with validation status */}
            <Alert
              severity={
                isSalvageExceptionResolved(
                  formValues.salvageValue ?? null,
                  formValues.deployedCostBasis ?? null
                )
                  ? 'success'
                  : 'error'
              }
              icon={
                isSalvageExceptionResolved(
                  formValues.salvageValue ?? null,
                  formValues.deployedCostBasis ?? null
                ) ? (
                  <CheckCircle />
                ) : (
                  <Error />
                )
              }
            >
              <Typography variant="body2">
                {isSalvageExceptionResolved(
                  formValues.salvageValue ?? null,
                  formValues.deployedCostBasis ?? null
                )
                  ? 'Salvage value is valid'
                  : 'Salvage value should be less than 50% of the deployed cost basis'}
              </Typography>
            </Alert>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
